import type { BaseResponse, EmptyResponse, HttpClient, Result } from '@/lib/http/HttpClient';

export type AdminUserRole = 'user' | 'admin' | 'super-admin';

export type AdminUser = {
  email: string;
  firstName?: string;
  lastName?: string;
  role: AdminUserRole;
  modules: string[];
  companyName: string;
  lastLogin?: string;
};

export interface ListUsersResponse extends BaseResponse {
  users: AdminUser[];
}

export type CompanyCount = { name: string; userCount: number };
export interface ListCompaniesResponse extends BaseResponse {
  companies: CompanyCount[];
}

export interface ListModulesResponse extends BaseResponse {
  modules: string[];
}

export type CreateUserPayload = {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  role: 'user' | 'admin';
  modules: string[];
  companyName: string;
};

export type UpdateUserPayload = {
  role?: 'user' | 'admin';
  companyName?: string;
  modules?: string[];
  firstName?: string;
  lastName?: string;
};

export class AdminApiClient {
  private readonly http: HttpClient;
  constructor(http: HttpClient) {
    this.http = http;
  }

  listUsers(): Promise<Result<ListUsersResponse>> {
    return this.http.get<ListUsersResponse>('/api/auth/users');
  }

  createUser(payload: CreateUserPayload): Promise<Result<EmptyResponse>> {
    return this.http.post<EmptyResponse>('/api/auth/users/create', payload);
  }

  updateUser(email: string, payload: UpdateUserPayload): Promise<Result<EmptyResponse>> {
    return this.http.put<EmptyResponse>(`/api/auth/users/${encodeURIComponent(email)}`, payload);
  }

  resetUserPassword(email: string, newPassword: string): Promise<Result<EmptyResponse>> {
    return this.http.post<EmptyResponse>(`/api/auth/users/${encodeURIComponent(email)}/password`, {
      userEmail: email,
      newPassword,
    });
  }

  deleteUser(email: string): Promise<Result<EmptyResponse>> {
    return this.http.delete<EmptyResponse>(`/api/auth/users/${encodeURIComponent(email)}`);
  }

  listCompanies(options?: RequestInit): Promise<Result<ListCompaniesResponse>> {
    return this.http.get<ListCompaniesResponse>('/api/auth/companies', options);
  }

  createCompany(name: string): Promise<Result<EmptyResponse>> {
    return this.http.post<EmptyResponse>('/api/auth/companies', { name });
  }

  renameCompany(name: string, newName: string): Promise<Result<EmptyResponse>> {
    return this.http.put<EmptyResponse>(`/api/auth/companies/${encodeURIComponent(name)}`, {
      name: newName,
    });
  }

  reassignCompanyUsers(name: string, newCompany: string): Promise<Result<EmptyResponse>> {
    return this.http.put<EmptyResponse>(`/api/auth/companies/${encodeURIComponent(name)}/reassign`, {
      newCompany,
    });
  }

  deleteCompany(name: string, cascade: boolean): Promise<Result<EmptyResponse>> {
    const query = cascade ? '?cascade=true' : '?cascade=false';
    return this.http.delete<EmptyResponse>(`/api/auth/companies/${encodeURIComponent(name)}${query}`);
  }

  listModules(): Promise<Result<ListModulesResponse>> {
    return this.http.get<ListModulesResponse>('/api/auth/modules');
  }
}
